import type {SketchGroup} from '../../mesh/sketch';
import {SketchProfileFeatureBinding} from '../binding/SketchProfileFeatureBinding';
import {buildRuntimeSketchEntitiesFromGroup, buildRuntimeSketchPlaneFromGroup} from '../../utils/feature';

/**
 * 草图轮廓绑定构造输入。
 */
export interface SketchProfileBindingPayloadBuildInput {
    /** 草图 ID。 */
    sketchId: string;
    /** 草图组实例。 */
    sketchGroup: SketchGroup | null;
    /** 轮廓选择结果。 */
    contours: Array<{
        contourSourceObjectId?: string;
        contourKey?: string;
        regionGeometry: ProfileRegionGeometry;
        regionIdentity?: PersistedRegionIdentity;
    }>;
}

/**
 * 草图轮廓绑定载荷构造器。
 */
export class SketchProfileBindingPayloadBuilder {
    /**
     * 构建草图轮廓绑定载荷。
     * @param input 构造输入。
     * @returns 绑定载荷。
     */
    public static build(input: SketchProfileBindingPayloadBuildInput): SerializedSketchProfileFeatureBinding {
        /**
         * 身份边界：会话内选择结果携带运行时 uuid，绑定是跨会话引用，必须在此统一转为持久 ID。
         * 草图组不可用或对象无持久身份（派生显示对象）时保留原值，由解析层按 uuid 兜底。
         */
        const sketchGroup = input.sketchGroup;
        const toPersistentId = (runtimeId: string | undefined): string | undefined => {
            if (!runtimeId || !sketchGroup) {
                return runtimeId;
            }
            return sketchGroup.resolvePersistentIdByRuntimeUuid(runtimeId) ?? runtimeId;
        };
        const sketchId = sketchGroup?.persistentId ?? input.sketchId;

        return SketchProfileFeatureBinding.createPayload({
            sketchId,
            contours: input.contours.map((contour) => ({
                sketchId,
                    contourSourceObjectId: toPersistentId(contour.contourSourceObjectId),
                    contourKey: toPersistentId(contour.contourKey),
                    regionGeometry: contour.regionGeometry,
                    regionIdentity: contour.regionIdentity,
                })),
            runtimeSketchPlane: input.sketchGroup ? buildRuntimeSketchPlaneFromGroup(input.sketchGroup) : undefined,
            runtimeSketchEntities: input.sketchGroup ? buildRuntimeSketchEntitiesFromGroup(input.sketchGroup) : undefined,
        });
    }
}
